import React, { Component } from "react";
import ThemeUtils from "./ThemeUtils";
import ColorPicker from "./ColorPicker";
import StyleView from "./StyleView";
import ModalController from "./ModalController";
import "./Styler.css";

class Styler extends Component {
  constructor(props) {
    super(props);
    this.state = {
      themeStyle: "Minimal",
      paletteGenerationType: "Split Complement",
      randomizePaletteGenerationType: true,
      randomizeThemeStyle: true,
    };
  }

  componentDidMount = () => {
    this.setRandomTheme(null, false, false);
  };

  setRandomTheme = (event, randomizeThemeStyle, randomizePaletteGenerationType) => {
    const bkgColor = ThemeUtils.getRandomBkgColor();

    this.updateAllColorsFromBackground(
      bkgColor,
      randomizePaletteGenerationType ?? this.state.randomizePaletteGenerationType,
      randomizeThemeStyle ?? this.state.randomizeThemeStyle,
    );
  };

  updateAllColorsFromBackground = (bkgColor, randomizePaletteGenerationType = false, randomizeThemeStyle = false) => {
    ThemeUtils.generateSecondaryPalettes(bkgColor);

    const themeStyle = randomizeThemeStyle ? ThemeUtils.getRandomThemeStyle() : this.state.themeStyle;
    const paletteGenerationType = randomizePaletteGenerationType
      ? ThemeUtils.getRandomPaletteGenerationType()
      : this.state.paletteGenerationType;
    const textColor = ThemeUtils.getTextColorFromBackgroundColor(bkgColor);
    const { primaryAccColor, secondaryAccColor } = ThemeUtils.getAccentColorsFromBackgroundColor(
      paletteGenerationType,
      bkgColor,
    );

    this.setState(
      {
        bkgColor: bkgColor,
        bkgColorIsValid: true,
        textColor: textColor,
        textColorIsValid: true,
        acc1Color: primaryAccColor,
        acc1ColorIsValid: true,
        acc2Color: secondaryAccColor,
        acc2ColorIsValid: true,
        themeStyle: themeStyle,
        paletteGenerationType: paletteGenerationType,
      },
      () => {
        this.updateTheme();
      },
    );
  };

  updateAccColors = () => {
    const { primaryAccColor, secondaryAccColor } = ThemeUtils.getAccentColorsFromBackgroundColor(
      this.state.paletteGenerationType,
      this.state.bkgColor,
    );

    this.setState(
      {
        acc1Color: primaryAccColor,
        acc1ColorIsValid: true,
        acc2Color: secondaryAccColor,
        acc2ColorIsValid: true,
      },
      () => {
        this.updateTheme();
      },
    );
  };

  updateTheme() {
    if (!(this.state.bkgColor && this.state.textColor && this.state.acc1Color && this.state.acc2Color)) return;

    if (
      this.appliedThemeStyle === this.state.themeStyle &&
      ThemeUtils.equals(this.appliedBkgColor, this.state.bkgColor) &&
      ThemeUtils.equals(this.appliedTextColor, this.state.textColor) &&
      ThemeUtils.equals(this.appliedAcc1Color, this.state.acc1Color) &&
      ThemeUtils.equals(this.appliedAcc2Color, this.state.acc2Color)
    )
      return;

    //console.time("updateTheme");

    ThemeUtils.applyTheme(
      this.state.themeStyle,
      this.state.bkgColor,
      this.state.textColor,
      this.state.acc1Color,
      this.state.acc2Color,
    );

    this.appliedThemeStyle = this.state.themeStyle;
    this.appliedBkgColor = this.state.bkgColor;
    this.appliedTextColor = this.state.textColor;
    this.appliedAcc1Color = this.state.acc1Color;
    this.appliedAcc2Color = this.state.acc2Color;

    //console.timeEnd("updateTheme");
  }

  setBackgroundColor = (color) => {
    this.setState(
      {
        bkgColor: color,
        bkgColorIsValid: color.isValid() || color.toString() === "",
      },
      () => {
        if (this.state.bkgColorIsValid) this.updateAllColorsFromBackground(color);
      },
    );
  };

  setTextColor = (color) => {
    this.setState(
      {
        textColor: color,
        textColorIsValid: color.isValid() || color.toString() === "",
      },
      () => {
        if (this.state.textColorIsValid) this.updateTheme();
      },
    );
  };

  setPrimaryAccentColor = (color) => {
    this.setState(
      {
        acc1Color: color,
        acc1ColorIsValid: color.isValid() || color.toString() === "",
      },
      () => {
        if (this.state.acc1ColorIsValid) this.updateTheme();
      },
    );
  };

  setSecondaryAccentColor = (color) => {
    this.setState(
      {
        acc2Color: color,
        acc2ColorIsValid: color.isValid() || color.toString() === "",
      },
      () => {
        if (this.state.acc2ColorIsValid) this.updateTheme();
      },
    );
  };

  setPaletteGenerationType = (paletteGenerationType, skipThemeUpdate = false) => {
    this.setState(
      {
        paletteGenerationType: paletteGenerationType,
      },
      () => {
        if (paletteGenerationType !== "Custom") this.updateAccColors();
      },
    );
  };

  setThemeStyle = (themeStyle) => {
    this.setState(
      {
        themeStyle: themeStyle,
      },
      () => {
        this.updateTheme();
      },
    );
  };

  handleBkgInputChange = (e) => {
    const color = ThemeUtils.getColor(e.target.value);
    this.setBackgroundColor(color);
  };

  handleTextInputChange = (e) => {
    const color = ThemeUtils.getColor(e.target.value);
    this.setTextColor(color);
  };

  handleAcc1InputChange = (e) => {
    const color = ThemeUtils.getColor(e.target.value);
    this.setPrimaryAccentColor(color);
  };

  handleAcc2InputChange = (e) => {
    const color = ThemeUtils.getColor(e.target.value);
    this.setSecondaryAccentColor(color);
  };

  handlePaletteGenerationTypeChange = (e) => {
    const paletteGenerationType = e.target.value;
    this.setPaletteGenerationType(paletteGenerationType);
  };

  handleThemeStyleChange = (e) => {
    const themeStyle = e.target.value;
    this.setThemeStyle(themeStyle);
  };

  handleRandomizePaletteGenerationTypeChange = (e) => {
    this.setState({
      randomizePaletteGenerationType: e.target.checked,
    });
  };

  handleRandomizeThemeStyleChange = (e) => {
    this.setState({
      randomizeThemeStyle: e.target.checked,
    });
  };

  getBkgColorPicker = () => {
    const p = ThemeUtils.getPalettes();
    return <ColorPicker categories={[p.sat10, p.sat20]} onColorClick={this.setBackgroundColor} />;
  };

  getTextColorPicker = () => {
    const p = ThemeUtils.getPalettes();
    return (
      <ColorPicker
        categories={[p.monotone, p.grayscale]}
        contrastingColor={this.state.bkgColor}
        onColorClick={this.setTextColor}
      />
    );
  };

  getAcc1ColorPicker = () => {
    const p = ThemeUtils.getPalettes();
    return (
      <ColorPicker
        categories={[p.complement, p.splitcomplement, p.triad, p.tetrad, p.analogous]}
        contrastingColor={this.state.bkgColor}
        onColorClick={this.setPrimaryAccentColor}
      />
    );
  };

  getAcc2ColorPicker = () => {
    const p = ThemeUtils.getPalettes();
    return (
      <ColorPicker
        categories={[p.complement, p.splitcomplement, p.triad, p.tetrad, p.analogous]}
        contrastingColor={this.state.bkgColor}
        onColorClick={this.setSecondaryAccentColor}
      />
    );
  };

  getStyleView = () => {
    return <StyleView themeStyle={this.state.themeStyle} />;
  };

  getValidityClassForBkgInput = () => {
    return this.state.bkgColorIsValid ? "" : " Invalid";
  };

  getValidityClassForTextInput = () => {
    return this.state.textColorIsValid ? "" : " Invalid";
  };

  getValidityClassForAcc1Input = () => {
    return this.state.acc1ColorIsValid ? "" : " Invalid";
  };

  getValidityClassForAcc2Input = () => {
    return this.state.acc2ColorIsValid ? "" : " Invalid";
  };

  getVisibilityClassForCustomFields = () => {
    return this.state.paletteGenerationType === "Custom" ? "" : " Hidden";
  };

  getVisibilityClassForToneCriteria = () => {
    return this.state.paletteGenerationType === "Custom" ? " Hidden" : "";
  };

  render() {
    const themeClasses = "Themed";

    return (
      <div className="StylerDiv">
        <div className="StylerInfoDiv">
          <span className="StylerIntro SecondaryText">
            <b>PROTOS</b> is a rapid prototyping tool that helps you quickly pick a look and feel for your product,
            which is both visually appealing and compliant with{" "}
            <a href="https://www.w3.org/TR/WCAG/#visual-audio-contrast">WCAG 2.0</a>
          </span>
          <span className="StylerDesc SecondaryText">
            Supported color input formats:
            <br />
            <ul>
              <li>
                <b>Hex</b> ("#000", "000", "#369C", "369C", "#f0f0f6", "f0f0f6", "#f0f0f688", "f0f0f688")
              </li>
              <li>
                <b>RGB, RGBA</b> ("rgb(255, 0, 0)", "rgb 255 0 0", "rgba (255, 0, 0, .5)", "rgba 255 0 255 .25")
              </li>
              <li>
                <b>HSL, HSLA, HSV, HSVA</b> ("hsl(0, 50%, 50%)", "hsla(120, 75%, 50%, 50%)", "hsl 180 1 .5", "hsla 315 1
                .5 .5")
              </li>
            </ul>
          </span>
        </div>
        <div className="StylerPanelDiv HighPanel">
          <div className="StylerInputWithLabelDiv">
            <span className="InputLabel SecondaryText">BACKGROUND COLOR:</span>
            <div className="StylerInputWithSelectorDiv">
              <input
                className={"Input " + themeClasses + " " + this.getValidityClassForBkgInput()}
                placeholder="Background Color"
                value={this.state.bkgColor && this.state.bkgColor.getOriginalInput()}
                onChange={this.handleBkgInputChange}
              />
              <div className="VerticalSeparator" />
              <ModalController modalContent={this.getBkgColorPicker()} closeOnInsideClick>
                <button className={"Small Button " + themeClasses}>
                  <i className="fas fa-expand"></i>
                </button>
              </ModalController>
            </div>
          </div>
          <div className="StylerInputWithLabelDiv">
            <div className="HorizontalSeparator" />
            <span className="InputLabel SecondaryText">PALETTE GENERATION TYPE:</span>
            <div className={"SelectDiv " + themeClasses}>
              <select
                className={"Select " + themeClasses}
                value={this.state.paletteGenerationType}
                onChange={this.handlePaletteGenerationTypeChange}
              >
                {ThemeUtils.getPaletteGenerationTypes().map((item, index) => (
                  <option className={"Option " + themeClasses} key={index}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={"StylerInputWithLabelDiv" + this.getVisibilityClassForCustomFields()}>
            <div className="HorizontalSeparator" />
            <span className="InputLabel SecondaryText">TEXT COLOR:</span>
            <div className="StylerInputWithSelectorDiv">
              <input
                className={"Input " + themeClasses + " " + this.getValidityClassForTextInput()}
                placeholder="Text Color"
                value={this.state.textColor && this.state.textColor.getOriginalInput()}
                onChange={this.handleTextInputChange}
              />
              <div className="VerticalSeparator" />
              <ModalController modalContent={this.getTextColorPicker()} closeOnInsideClick>
                <button className={"Small Button " + themeClasses}>
                  <i className="fas fa-expand"></i>
                </button>
              </ModalController>
            </div>
          </div>
          <div className={"StylerInputWithLabelDiv" + this.getVisibilityClassForCustomFields()}>
            <div className="HorizontalSeparator" />
            <span className="InputLabel SecondaryText">PRIMARY ACCENT COLOR:</span>
            <div className="StylerInputWithSelectorDiv">
              <input
                className={"Input " + themeClasses + " " + this.getValidityClassForAcc1Input()}
                placeholder="Primary Accent Color"
                value={this.state.acc1Color && this.state.acc1Color.getOriginalInput()}
                onChange={this.handleAcc1InputChange}
              />
              <div className="VerticalSeparator" />
              <ModalController modalContent={this.getAcc1ColorPicker()} closeOnInsideClick>
                <button className={"Small Button " + themeClasses}>
                  <i className="fas fa-expand"></i>
                </button>
              </ModalController>
            </div>
          </div>
          <div className={"StylerInputWithLabelDiv" + this.getVisibilityClassForCustomFields()}>
            <div className="HorizontalSeparator" />
            <span className="InputLabel SecondaryText">SECONDARY ACCENT COLOR:</span>
            <div className="StylerInputWithSelectorDiv">
              <input
                className={"Input " + themeClasses + " " + this.getValidityClassForAcc2Input()}
                placeholder="Secondary Accent Color"
                value={this.state.acc2Color && this.state.acc2Color.getOriginalInput()}
                onChange={this.handleAcc2InputChange}
              />
              <div className="VerticalSeparator" />
              <ModalController modalContent={this.getAcc2ColorPicker()} closeOnInsideClick>
                <button className={"Small Button " + themeClasses}>
                  <i className="fas fa-expand"></i>
                </button>
              </ModalController>
            </div>
          </div>
          <div className="StylerInputWithLabelDiv">
            <div className="HorizontalSeparator" />
            <span className="InputLabel SecondaryText">THEME STYLE:</span>
            <div className={"SelectDiv " + themeClasses}>
              <select
                className={"Select " + themeClasses}
                value={this.state.themeStyle}
                onChange={this.handleThemeStyleChange}
              >
                {ThemeUtils.getThemeStyles().map((item, index) => (
                  <option className={"Option " + themeClasses} key={index}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="StylerCheckboxesDiv">
            <div className="CheckboxDiv">
              <input
                className={"Checkbox " + themeClasses}
                type="checkbox"
                id="randomPaletteGenerationTypeCheck"
                checked={this.state.randomizePaletteGenerationType}
                onChange={this.handleRandomizePaletteGenerationTypeChange}
              />
              <label className="Label SecondaryText" htmlFor="randomPaletteGenerationTypeCheck">
                Randomize Palette Generation Type.
              </label>
            </div>
            <div className="HorizontalSeparator" />
            <div className="CheckboxDiv">
              <input
                className={"Checkbox " + themeClasses}
                type="checkbox"
                id="randomThemeStyleCheck"
                checked={this.state.randomizeThemeStyle}
                onChange={this.handleRandomizeThemeStyleChange}
              />
              <label className="Label SecondaryText" htmlFor="randomThemeStyleCheck">
                Randomize Theme Style.
              </label>
            </div>
          </div>
          <div className="StylerButtonsDiv">
            <button className={"Button " + themeClasses} onClick={this.setRandomTheme}>
              RANDOM
            </button>
            <div className="VerticalSeparator" />
            <ModalController modalContent={this.getStyleView()}>
              <button className={"Button Secondary " + themeClasses} onClick={this.showThemeDetails}>
                OUTPUT
              </button>
            </ModalController>
          </div>
        </div>
      </div>
    );
  }
}

export default Styler;
